import { inject, Injectable } from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { Store } from "@ngrx/store";
import { driver, Driver, DriveStep } from "driver.js";
import { AppState } from "../../../store/app.state";
import { setTourPerfil } from "../../../store/tour-perfil/tour-perfil.actions";

@Injectable({
  providedIn: 'root'
})
export class PerfilTourService {
  private _TOUR_KEY_PERFIL = 'perfilTourCompleted';
  private driverInstance: Driver | null = null;
  appState = inject(Store<AppState>);
  private router = inject(Router);
  constructor() { }

  private initDriver(): Driver {




    if (!this.driverInstance) {
      this.driverInstance = driver({
        showProgress: true,
        showButtons: ['next', 'previous', 'close'],
        steps: [],
        popoverClass: 'driverjs-theme',
        nextBtnText: 'Siguiente',
        prevBtnText: 'Anterior',
        doneBtnText: 'Entendido',

        onDestroyed: () => {
          this.driverInstance = null;
          if (typeof document !== 'undefined') {
            document.body.style.overflow = '';
          }
        }
      });
    }
    return this.driverInstance;
  }

  /**
   * Busca el enlace de perfil visible (desktop o móvil).
   */
  private findVisibleProfileLink(): Element | null {
    if (typeof document === 'undefined') return null;
    const candidates = Array.from(document.querySelectorAll('[data-tour="profile-link"]'));
    const isVisible = (el: Element) => {
      const htmlEl = el as HTMLElement;
      if (!htmlEl) return false;
      const style = window.getComputedStyle(htmlEl);
      const rect = htmlEl.getBoundingClientRect();
      return (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        rect.width > 0 &&
        rect.height > 0
      );
    };

    for (const el of candidates) {
      if (isVisible(el)) return el;
    }
    return null;
  }

  /**
   * Intenta encontrar el enlace visible con reintentos.
   * En móviles, si el menú está cerrado, intenta abrirlo.
   */
  private findProfileWithRetry(maxAttempts = 5, delayMs = 250, attempt = 1, tryOpenMobileMenu = true, onFound?: (el: Element) => void, onFail?: () => void): void {
    const tryFind = () => {
      const el = this.findVisibleProfileLink();
      if (el) {
        onFound && onFound(el);
        return;
      }

      // Si estamos en móvil y no se encontró, intenta abrir el menú móvil una vez
      if (tryOpenMobileMenu && typeof window !== 'undefined' && window.innerWidth < 1024) {
        const toggleBtn = document.querySelector('header button.lg\\:hidden');
        if (toggleBtn) {
          (toggleBtn as HTMLElement).click();
        }
      }

      if (attempt < maxAttempts) {
        setTimeout(() => this.findProfileWithRetry(maxAttempts, delayMs, attempt + 1, false, onFound, onFail), delayMs);
      } else {
        onFail && onFail();
      }
    };

    // Espera al siguiente frame para asegurar layout listo
    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(tryFind);
    } else {
      setTimeout(tryFind, 0);
    }
  }

  /**
   * Espera a que exista y esté visible un selector en el DOM.
   */
  private waitForElement(selector: string, maxAttempts = 6, delayMs = 250, onFound?: (el: Element) => void, onFail?: () => void): void {
    const attemptFind = (attempt = 1) => {
      const el = document.querySelector(selector);
      const isVisible = (node: Element | null) => {
        if (!node) return false;
        const htmlEl = node as HTMLElement;
        const style = window.getComputedStyle(htmlEl);
        const rect = htmlEl.getBoundingClientRect();
        return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
      };
      if (isVisible(el)) {
        onFound && onFound(el!);
        return;
      }
      if (attempt < maxAttempts) {
        setTimeout(() => attemptFind(attempt + 1), delayMs);
      } else {
        onFail && onFail();
      }
    };
    attemptFind(1);
  }

  /**
   * Tras navegar a `targetUrl`, espera el elemento de cuenta y lanza el tour.
   */
  private runPerfilAccountTourAfterNavigation(targetUrl: string = '/auth/perfil'): void {
    const sub = this.router.events.subscribe(ev => {
      if (ev instanceof NavigationEnd && ev.urlAfterRedirects.startsWith(targetUrl)) {
        sub.unsubscribe();
        if (typeof window !== 'undefined') {
          window.scrollTo({ top: 0, behavior: 'auto' });
        }
        setTimeout(() => {
          this.waitForElement('[data-tour="profile-account"]', 8, 250, () => {
            this.perfilTour();
          });
        }, 120);
      }
    });
  }


  showTourPerfil(): void {
    this.initLocalStorage();
    const driverInstance = this.initDriver();

    this.findProfileWithRetry(
      6,
      250,
      1,
      true,
      (el) => {
        // Asegura que el elemento esté en viewport
        (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
        const steps: DriveStep[] = [
          {
            element: el as HTMLElement,
            popover: {
              title: '👤 Editar Perfil',
              description:
                'Haz clic aquí para ir a tu perfil donde podrás editar tu información personal.',
              side: 'bottom',
              align: 'start',
              showButtons: ['next'],
              nextBtnText: 'Ir a Perfil',
              onNextClick: () => {
                this.router.navigateByUrl('/auth/perfil');
                this.runPerfilAccountTourAfterNavigation('/auth/perfil');
                this.driverInstance?.destroy();
              }
            },

          }

        ];

        driverInstance.setSteps(steps);
        if (typeof document !== 'undefined') {
          document.body.style.overflow = 'hidden';
        }
        driverInstance.drive();
      },
      () => {
        const steps: DriveStep[] = [
          {
            element: 'body',
            popover: {
              title: '👤 Editar Perfil',
              description:
                'No pude encontrar el enlace de Perfil visible. En móvil, abre el menú para verlo.',
              side: 'bottom',
              align: 'center'
            }
          }
        ];
        driverInstance.setSteps(steps);
        if (typeof document !== 'undefined') {
          document.body.style.overflow = 'hidden';
        }
        driverInstance.drive();
      }
    );
  }

  destroy(): void {
    if (this.driverInstance) {
      this.driverInstance.destroy();
      this.driverInstance = null;
    }
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
  }

  /**
   * Marca el tour de perfil como completado y actualiza el estado global
   */
  private markPerfilTourCompleted(): void {
    localStorage.setItem(this._TOUR_KEY_PERFIL, 'true');
    this.appState.dispatch(setTourPerfil({ tourPerfil: true }));
  }

  /**
   * Verifica si hay un tour activo
   */
  isActive(): boolean {
    return this.driverInstance !== null;
  }

  perfilTour() {
    const driverInstance = this.initDriver();
    const steps: DriveStep[] = [
      {
        element: '[data-tour="profile-account"]',
        popover: {
          title: 'Tu información de perfil',
          description: 'Aquí puedes actualizar tus datos personales y bancarios',
          side: 'bottom',
          align: 'start',
          showButtons: ['next'],
          nextBtnText: 'Siguiente',
          onNextClick: () => {
            // Cerrar el tour actual y navegar, luego reanudar en la siguiente ruta
            this.driverInstance?.destroy();
            this.router.navigateByUrl('/auth/perfil/payments');
            this.runPerfilPaymentsTourAfterNavigation('/auth/perfil/payments');
          }
        }
      },
      {
        element: '[data-tour="profile-payments"]',
        popover: {
          title: 'Métodos de pago',
          description: 'Aquí puedes agregar y gestionar tus métodos de pago',
          side: 'bottom',
          align: 'start',
          showButtons: ['next'],
          nextBtnText: 'Siguiente',
          onNextClick: () => {
            // Cerrar y reanudar el tour en la siguiente sección

            this.router.navigateByUrl('/auth/perfil/favorites-establishment');
            this.runPerfilFavoritesTourAfterNavigation('/auth/perfil/favorites-establishment');
          }
        }
      }, {
        element: '[data-tour="profile-favorites"]',
        popover: {
          title: 'Favoritos',
          description: 'Aquí puedes ver y gestionar tus establecimientos favoritos',
          side: 'bottom',
          align: 'start',
          showButtons: ['next'],
          nextBtnText: 'Entendido',
          onNextClick: () => {
            // Cerrar y reanudar el tour en la siguiente sección
            this.driverInstance?.destroy();
            this.router.navigateByUrl('/auth/perfil/devoluciones');
            this.runPerfilDevolucionesTourAfterNavigation('/auth/perfil/devoluciones');
          }
        }
      },
      {
        element: '[data-tour="profile-devoluciones"]',
        popover: {
          title: 'Devoluciones',
          description: 'Aquí puedes ver y gestionar tus devoluciones',
          side: 'bottom',
          align: 'start',
          showButtons: ['next'],
          nextBtnText: 'Entendido',
          onNextClick: () => {
            this.driverInstance?.destroy();
            this.router.navigateByUrl('/auth/perfil/recargos');
            this.runPerfilRecargosTourAfterNavigation('/auth/perfil/recargos');
          }
        }
      },
      {
        element: '[data-tour="profile-recargos"]',
        popover: {
          title: 'Recargos',
          description: 'Aquí puedes ver y gestionar tus recargos',
          side: 'bottom',
          align: 'start',
          showButtons: ['next'],
          nextBtnText: 'Entendido',
          onNextClick: () => {
            this.markPerfilTourCompleted();
            this.driverInstance?.destroy();
            this.router.navigateByUrl('/auth/perfil');
          }
        }
      }
    ];
    driverInstance.setSteps(steps);
    if (typeof document !== 'undefined') {
      document.body.style.overflow = 'hidden';
    }
    driverInstance.drive();
  }

  /**
   * Tras navegar a payments, espera el elemento y muestra su paso
   */
  private runPerfilPaymentsTourAfterNavigation(targetUrl: string = '/auth/perfil/payments'): void {
    const sub = this.router.events.subscribe(ev => {
      if (ev instanceof NavigationEnd && ev.urlAfterRedirects.startsWith(targetUrl)) {
        sub.unsubscribe();
        setTimeout(() => {
          this.waitForElement('[data-tour="profile-payments"]', 8, 250, () => {
            const d = this.initDriver();
            d.setSteps([
              {
                element: '[data-tour="profile-payments"]',
                popover: {
                  title: 'Métodos de pago',
                  description: 'Aquí puedes agregar y gestionar tus métodos de pago',
                  side: 'bottom',
                  align: 'start',
                  showButtons: ['next'],
                  nextBtnText: 'Siguiente',
                  onNextClick: () => {
                    this.driverInstance?.destroy();
                    this.router.navigateByUrl('/auth/perfil/favorites-establishment');
                    this.runPerfilFavoritesTourAfterNavigation('/auth/perfil/favorites-establishment');
                  }
                }
              }
            ]);
            if (typeof document !== 'undefined') {
              document.body.style.overflow = 'hidden';
            }
            d.drive();
          });
        }, 120);
      }
    });
  }

  /**
   * Tras navegar a favorites, espera el elemento y muestra su paso
   */
  private runPerfilFavoritesTourAfterNavigation(targetUrl: string = '/auth/perfil/favorites-establishment'): void {
    const sub = this.router.events.subscribe(ev => {
      if (ev instanceof NavigationEnd && ev.urlAfterRedirects.startsWith(targetUrl)) {
        sub.unsubscribe();
        setTimeout(() => {
          this.waitForElement('[data-tour="profile-favorites"]', 8, 250, () => {
            const d = this.initDriver();
            d.setSteps([
              {
                element: '[data-tour="profile-favorites"]',
                popover: {
                  title: 'Favoritos',
                  description: 'Aquí puedes ver y gestionar tus establecimientos favoritos',
                  side: 'bottom',
                  align: 'start',
                  showButtons: ['next'],
                  nextBtnText: 'Entendido',
                  onNextClick: () => {
                    this.driverInstance?.destroy();
                    this.router.navigateByUrl('/auth/perfil/devoluciones');
                    this.runPerfilDevolucionesTourAfterNavigation('/auth/perfil/devoluciones');
                  }
                }
              }
            ]);
            if (typeof document !== 'undefined') {
              document.body.style.overflow = 'hidden';
            }
            d.drive();
          });
        }, 120);
      }
    });
  }

  /**
   * Tras navegar a devoluciones, espera el elemento y muestra su paso
   */
  private runPerfilDevolucionesTourAfterNavigation(targetUrl: string = '/auth/perfil/devoluciones'): void {
    const sub = this.router.events.subscribe(ev => {
      if (ev instanceof NavigationEnd && ev.urlAfterRedirects.startsWith(targetUrl)) {
        sub.unsubscribe();
        setTimeout(() => {
          this.waitForElement('[data-tour="profile-devoluciones"]', 8, 250, () => {
            const d = this.initDriver();
            d.setSteps([
              {
                element: '[data-tour="profile-devoluciones"]',
                popover: {
                  title: 'Devoluciones',
                  description: 'Aquí puedes ver y gestionar tus devoluciones',
                  side: 'bottom',
                  align: 'start',
                  showButtons: ['next'],
                  nextBtnText: 'Entendido',
                  onNextClick: () => {
                    this.driverInstance?.destroy();
                    this.router.navigateByUrl('/auth/perfil/recargos');
                    this.runPerfilRecargosTourAfterNavigation('/auth/perfil/recargos');
                  }
                }
              }
            ]);
            if (typeof document !== 'undefined') {
              document.body.style.overflow = 'hidden';
            }
            d.drive();
          });
        }, 120);
      }
    });
  }

  /**
   * Tras navegar a recargos, espera el elemento y muestra su paso
   */
  private runPerfilRecargosTourAfterNavigation(targetUrl: string = '/auth/perfil/recargos'): void {
    const sub = this.router.events.subscribe(ev => {
      if (ev instanceof NavigationEnd && ev.urlAfterRedirects.startsWith(targetUrl)) {
        sub.unsubscribe();
        setTimeout(() => {
          this.waitForElement('[data-tour="profile-recargos"]', 8, 250, () => {
            const d = this.initDriver();
            d.setSteps([
              {
                element: '[data-tour="profile-recargos"]',
                popover: {
                  title: 'Recargos',
                  description: 'Aquí puedes ver y gestionar tus recargos',
                  side: 'bottom',
                  align: 'start',
                  showButtons: ['next'],
                  nextBtnText: 'Entendido',
                  onNextClick: () => {
                    this.markPerfilTourCompleted();
                    this.driverInstance?.destroy();
                    if (typeof document !== 'undefined') {
                      document.body.style.overflow = '';
                    }
                  }
                }
              }
            ]);
            if (typeof document !== 'undefined') {
              document.body.style.overflow = 'hidden';
            }
            d.drive();
          });
        }, 120);
      }
    });
  }

  initLocalStorage(): void {
    const tourCompleted = localStorage.getItem(this._TOUR_KEY_PERFIL);
    if (!tourCompleted) {
      localStorage.setItem(this._TOUR_KEY_PERFIL, 'false');
      this.appState.dispatch(setTourPerfil({ tourPerfil: false }));
    } else {
      localStorage.setItem(this._TOUR_KEY_PERFIL, 'true');
      this.appState.dispatch(setTourPerfil({ tourPerfil: JSON.parse(tourCompleted) }));
    }
  }

}
